import React, { useState, useEffect } from "react";
import "./IdologyVerification.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { verifyUserProfile } from "@/api/LoginSignup";
import {
  checkDeletedUserEmail,
  checkLoginStatus,
  sendEmailLink,
} from "@/api/myProfile";
import { validateForm } from "@/utils/formValidator";
import { getUserProfileVerificationDetails } from "@/api/myProfile";
import { FaUser, FaCalendarAlt, FaLock } from "react-icons/fa";
import LoadingText from "../loader/LoadingText";
import LoadedText from "../loader/LoadedText";
import IdologyQuestions from "./IdologyQuestions";
import { trackApiCall, trackButtonClick, trackPageMount } from "@/utils/analytics";
import { useDispatch, useSelector } from "react-redux";
import { trackEvent } from "@/utils/analytics";

const LockedAccountModal = ({
  show,
  handleClose,
  showSendLink,
  emailSend,
  setEmailSend,
}) => {
  // const [emailSend,setEmailSend] = useState(false)
  const sendLinkToEmail = async () => {
    try {
      const data = await sendEmailLink();
      if (data?.status === "SUCCESS") {
        setEmailSend(true);
      } else {
        setEmailSend(false);
        console.log(data, "data error");
      }
    } catch (error) {
      console.log(error);
      setEmailSend(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="idology-modal-header">
          <h4>Profile Verification</h4>
          <button className="idology-close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-content">
          {emailSend ? (
            <>
              <p className="fs-14 robo-light">
                We've sent a message to your registered email address. Please
                follow the instructions to complete your verification by
                uploading a valid government issued ID.
              </p>
            </>
          ) : (
            <>
              <p className="fs-12 robo-light">
                Your account has been locked because you have failed the
                authentication process three times. Please contact Customer Care
                at (855) 882-1314 or customercare@hubzu.com for further
                assistance.
                <br />
                {showSendLink && (
                  <>
                    Alternatively, Hubzu may be able to manually verify your
                    identity using your government-issued identification. Please
                    click below to receive additional information and
                    instructions about this process.
                  </>
                )}
              </p>
              {showSendLink && (
                <p className="text-center fs-12 robo-light">
                  <button
                    className="send-link btn btn-primary"
                    onClick={sendLinkToEmail}
                  >
                    Scan Link{" "}
                  </button>
                </p>
              )}
            </>
          )}
        </div>
        {/*
        <button className="modal-close-button" onClick={handleClose}>
          Close
        </button> */}
      </div>
    </div>
  );
};

const IdologyVerification = ({
  show,
  handleClose,
  position,
  userData,
  userProfileData,
  setShowIdologyModal,
  setIdologySuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [remainingAttemptCount, setRemainingAttemptCount] = useState(null);
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const [apiErrorAuth, setApiErrorAuth] = useState("");
  const [showIdolodyQuestions, setShowIdolodyQuestions] = useState(false);
  const [question, setQuestions] = useState([]);
  const [showSendLink, setShowSendLink] = useState(false);
  const [emailSend, setEmailSend] = useState(false);
  const profileInfo = useSelector((state) => state.user.profileInfo);



  // State for form fields
  const [formData, setFormData] = useState({
    userBasicDetails: {
      firstName: "",
      lastName: "",
      emailId: "",
    },
    dob: "",
    ssn: "",
  });

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      userBasicDetails: {
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        emailId: userData?.emailId || "",
      },
      dob: userData?.dob || "",
      ssn: userData?.ssn || "",
    }));
  }, [userData]);

  const fetchUserAttempts = async () => {
    try {
      const response = await getUserProfileVerificationDetails();
      setRemainingAttemptCount(
        response?.data?.userIdologyDetailsDto?.remainingAttemptCount
      ); // Default to 3
      const showLink =
        response?.data?.userIdologyDetailsDto?.resetCountExceeded &&
        response?.data?.userIdologyDetailsDto?.scanAvailable &&
        !response?.data?.userIdologyDetailsDto?.mailSent;
      setShowSendLink(showLink);
    } catch (error) {
      console.error("Error fetching remaining attempt count:", error);
    }
  };
  // Fetch remaining attempt count from API
  useEffect(() => {
    if (show) {
      fetchUserAttempts();
    }
  }, [show]);

  useEffect(() => {
    fetchUserAttempts();
    setApiErrorMessage("");

    setFormData((prevData) => ({
      ...prevData,
      userBasicDetails: {
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        emailId: userData?.emailId || "",
      },
      dob: "",
      ssn: "",
    }));
  }, []);

  useEffect(() => {
    trackPageMount({
      eventName: "idology_page_view",
      eventCategory: "Idology",
      eventLabel: "Idology verification page loaded"
    });
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;

    let updatedFormData;
    if (id === "dob" || id === "ssn") {
      updatedFormData = {
        ...formData,
        [id]: value,
      };
    } else {
      updatedFormData = {
        ...formData,
        userBasicDetails: {
          ...formData.userBasicDetails,
          [id]: value,
        },
      };
    }

    setFormData(updatedFormData);

    // Validate the changed field
    const validationErrors = validateForm({ [id]: value }, [id]);

    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      [id]: validationErrors[id] || "",
    }));
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setApiErrorMessage("");
    setErrorMessage("");

    // Construct full validation input
    const formValues = {
      ...formData.userBasicDetails,
      dob: formData.dob,
      ssn: formData.ssn,
    };

    const validationErrors = validateForm(
      formValues,
      Object.keys(formValues),
      true
    );

    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setLoading(false);
      return;
    }

    trackButtonClick({
      eventName: "idology_submit",
      eventCategory: "My Shopper",
      eventSource: "Global alerts idology from alerts",
    });

    try {
      const response = await verifyUserProfile(formData);

      if (response?.status === "SUCCESS") {
        if (response?.data?.questions?.length > 0) {
          setShowIdolodyQuestions(true);
          setQuestions(response?.data?.questions);
          // setShowIdologyModal(false);
        } else {
          trackApiCall({
            eventName: "idology_verification_success",
            eventCategory: "My Shopper",
            eventSource: "Global alerts idology from my profile",
            eventParams: { idology_verification: "successful" },
          });

          if (profileInfo?.userDetails?.phoneNumberVerified === true) {
            trackEvent({
              name: "ready_to_bid",
              category: "New-Bid Wizard",
              eventSource: "on Ideology Verification",
              eventLabel: "",
            });
          }

          toast.success("Your profile is verified successfully.");
          setErrorMessage("");
          setRemainingAttemptCount(3); // Reset attempts on success
          setShowIdologyModal(false);
          setIdoSuccess();
        }

        // check for deleted user api calls if idology is not verified
        if (userProfileData?.idologyVerificationRequired) {
          try {
            const encodedEmail = encodeURIComponent(
              formData?.userBasicDetails.emailId,
            );
            const queryString = `emailId=${encodedEmail}`;
            const deletedUserCheck = await checkDeletedUserEmail(queryString);
            if (
              deletedUserCheck?.data?.status === "SUCCESS" &&
              deletedUserCheck?.data?.data === true
            ) {
              toast.error(
                "This email is associated with a deleted account. Please contact support for assistance.",
              );
            }
          } catch (error) {
            console.error("Error checking deleted user email:", error);
          }
        }
      } else {
        let errorKey = response?.errorMessage;
        const remaining = response?.data?.remainingAttemptCount;
        setRemainingAttemptCount(remaining);
        const showLink =
          response?.data?.resetCountExceeded &&
          response?.data?.scanAvailable &&
          !response?.data?.mailSent;
        setShowSendLink(showLink);

        trackApiCall({
          eventName: "confirm_idology_failure",
          eventCategory: "My Shopper",
          eventSource: "Global alerts idology from my profile",
          eventParams: {
            error_type:
              response?.errorMessage === "errorIdologyAttemptsExceeded"
                ? "attempt_exceeded"
                : response?.errorMessage === "errorIdologyIdFailure" &&
                    response?.data?.remainingAttemptCount === 0
                  ? "attempt_exceeded"
                  : response?.errorMessage === "errorIdologyDuplicate"
                    ? "idology_duplicate"
                    : "idology_failure",
          },
        });

        if (errorKey === "errorIdologyIdFailure" && remaining > 0) {
          const message = `
          Please enter your first name, last name, last 4 digits of your Social Security Number and date of birth as they appear on government-issued documents.
          You have ${remaining} more attempt to log in before your account is locked. Please call Customer Care at (855) 882-1314 with questions or concerns.`;
          setApiErrorAuth("Authentication failed.");
          setApiErrorMessage(message);
        } else if (errorKey === "errorIdologyDuplicate") {
          // const message = `We found an existing profile with the provided details.
          // If you believe this is an error, please contact Customer Care at (855) 882-1314 or customercare@hubzu.com for assistance.`;
          const message = `Our records show, you already have an account on Hubzu with ${response?.data?.maskedDuplicateEmailID}. If you want to change the email id, please login to Hubzu.com and change the email Id. Please contact customer care at (855) 882-1314 or customercare@hubzu.com for any assistance.`;
          setApiErrorAuth("Authentication failed.");
          setApiErrorMessage(message);
        } else if (errorKey === "errorIdologySystemError") {
          const message = `We’re currently experiencing technical difficulties with the verification system.
          Please try again later or contact Customer Care at (855) 882-1314.`;
          setApiErrorMessage(message);
        } else if (errorKey === "errorIdologyAttemptsExceeded") {
          const message = ` Your account has been locked because you have failed the authentication process three times. Please contact Customer Care at (855) 882-1314 or customercare@hubzu.com for further assistance.`;
          setApiErrorMessage(message);
        } else if (
          errorKey === "errorIdologyIdFailure" &&
          remaining === 0 &&
          showSendLink
        ) {
          const message = ` Your account has been locked because you have failed the authentication process three times. Please contact Customer Care at (855) 882-1314 or customercare@hubzu.com for further assistance.
          Alternatively, Hubzu may be able to manually verify your identity using your government-issued identification. Please click below to receive additional information and instructions about this process.
          `;
          setApiErrorMessage(message);
        } else {
          setApiErrorMessage("Verification failed. Please try again.");
        }
      }
    } catch (error) {
      console.log(
        "🚀 ~ IdologyVerification.jsx:183 ~ handleVerify ~ error:",
        error
      );
      // Reduce remaining attempt count on failure
    } finally {
      setLoading(false);
    }
  };

  const closeIdologyModal = () => {
    setApiErrorMessage("");
    setFormData({ ...formData, dob: "", ssn: "" });
    setShowIdolodyQuestions(false);
    setEmailSend(false);
    setShowSendLink(false);
    handleClose();
  };

  const setIdoSuccess = () => {
    setIdologySuccess(true);
  };

  return (
    <div
      className={`idology-modal-container ${show ? "show" : ""} ${position}`}
      onClick={closeIdologyModal}
    >
      <div
        className="idology-modal-content"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {remainingAttemptCount === 0 ? (
          <LockedAccountModal
            show={true}
            handleClose={closeIdologyModal}
            showSendLink={showSendLink}
            setEmailSend={setEmailSend}
            emailSend={emailSend}
          />
        ) : (
          <>
            {!showIdolodyQuestions ? (
              <>
                <div className="idology-modal-header">
                  <h4>Profile Verification</h4>
                  <button
                    className="idology-close-button"
                    onClick={closeIdologyModal}
                  >
                    ×
                  </button>
                </div>

                <div className="idology-para-container">
                  <p className="idology-para-icon" />
                  <p className="idology-content-para">
                    We require the last four digits of your Social Security
                    number as an additional verification step to protect our
                    customers against potential fraud. The information is used
                    to verify your identity. We do not store your Social
                    Security number or date of birth in our database, and this
                    information is deleted as soon as your identity is verified.
                  </p>
                </div>
                <form
                  className="idology-form-container"
                  onSubmit={handleVerify}
                  noValidate
                >
                  {[
                    {
                      name: "firstName",
                      label: "First Name",
                      icon: <FaUser />,
                    },
                    { name: "lastName", label: "Last Name", icon: <FaUser /> },
                    {
                      name: "dob",
                      label: "Date of Birth (MM/DD/YYYY)",
                      icon: <FaCalendarAlt />,
                    },
                    {
                      name: "ssn",
                      label: "Last 4 Digits of SSN",
                      icon: <FaLock />,
                    },
                  ].map(({ name, label, icon }) => {
                    const isUserDetail =
                      name === "firstName" ||
                      name === "lastName" ||
                      name === "emailId";

                    const value = isUserDetail
                      ? formData.userBasicDetails[name]
                      : formData[name];

                    return (
                      <div
                        className="idology-form-group position-relative d-flex flex-column"
                        key={name}
                      >
                        <div className="d-flex align-items-center idology-form-fields">
                          <span className="idology-input-icon">{icon}</span>
                          <input
                            type={"text"}
                            id={name}
                            required
                            max={
                              name === "dob"
                                ? `${new Date().getFullYear()}-12-31`
                                : undefined
                            }
                            maxLength={name === "ssn" ? 4 : undefined}
                            value={value}
                            onChange={handleInputChange}
                            onFocus={(e) =>
                              // name === "dob" && (e.target.type = "date")
                              {
                                if (name === "dob") {
                                  e.target.type = "date";
                                  // Convert MM/DD/YYYY to YYYY-MM-DD format for date input
                                  if (value) {
                                    const [month, day, year] = value.split("/");
                                    const formattedDate = `${year}-${month}-${day}`;
                                    e.target.value = formattedDate;
                                  }
                                }
                              }
                            }
                            onBlur={(e) =>
                              // name === "dob" && (e.target.type = "text")
                              {
                                const timeStamp = e.target.value;

                                if (name === "dob") {
                                  e.target.type = "text";
                                  if (timeStamp) {
                                    try {
                                      // timeStamp is a date-only string (YYYY-MM-DD).
                                      // Parse the parts directly instead of `new Date(str)`,
                                      // which treats the value as UTC midnight and shifts
                                      // the day back in negative-offset zones (e.g. EST).
                                      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(
                                        timeStamp
                                      );
                                      if (match) {
                                        const [, year, month, day] = match;
                                        const formattedDate = `${month}/${day}/${year}`;

                                        setFormData((prevState) => ({
                                          ...prevState,
                                          dob: formattedDate,
                                        }));
                                      }
                                    } catch (error) {
                                      console.error(
                                        "Date parsing error:",
                                        error
                                      );
                                    }
                                  }
                                }
                              }
                            }
                            onKeyDown={(e) => {
                              if (name === "dob" && e.key === "Enter") {
                                e.target.blur();
                              }
                            }}
                            className={`idology-floating-input ${
                              fieldErrors[name]
                                ? "idology-border-danger"
                                : "idology-border-bottom"
                            }`}
                            placeholder=" "
                          />
                          <label
                            htmlFor={name}
                            className={`idology-floating-label ${
                              value ? "idology-filled" : ""
                            }`}
                          >
                            {label}
                          </label>
                        </div>

                        {/* ✅ Error Message Below Input */}
                        {fieldErrors[name] && (
                          <div
                            className="error-message"
                            role="alert"
                            aria-live="polite"
                            style={{
                              color: "#dc3545",
                              fontSize: "12px",
                              backgroundColor: "transparent",
                              marginTop: "4px",
                            }}
                          >
                            {fieldErrors[name]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {apiErrorMessage && (
                    <div
                      className="text-danger mt-3 mb-1"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      <h6 className="text-danger mb-0">{apiErrorAuth}</h6>
                      {apiErrorMessage}
                    </div>
                  )}
                  <div className="text-center">
                    <button
                      className="idology-verify-button btn btn-secondary"
                      style={{ background: "#118bf4" }}
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <LoadingText text="Verifying..." />
                      ) : (
                        <LoadedText text="Verify" />
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <IdologyQuestions
                handleClose={closeIdologyModal}
                questions={question}
                ssn={formData.ssn}
                dob={formData.dob}
                setQuestions={setQuestions}
                isFromAlerts={false}
                idoSuccess={setIdoSuccess}
                parentComponent="MyProfile"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IdologyVerification;
